import {Accordion, AccordionDetails, AccordionSummary, Alert} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import React, {useMemo, useState} from 'react'
import {useBoxesForSerialAdminQuery, useClearBoxSerialNumberMutation} from '../../../generated/graphql'
import {Box} from '../../../webui/Box'
import {Button} from '../../../webui/Button'
import {ButtonWithLoading} from '../../../webui/ButtonWithLoading'
import {Checkbox} from '../../../webui/Checkbox'
import {Dialog} from '../../../webui/Dialog'
import {DialogActions} from '../../../webui/DialogActions'
import {DialogContent} from '../../../webui/DialogContent'
import {DialogContentText} from '../../../webui/DialogContentText'
import {DialogTitle} from '../../../webui/DialogTitle'
import {TextField} from '../../../webui/inputs/TextField'
import {Table} from '../../../webui/table/Table'
import {TableBody} from '../../../webui/table/TableBody'
import {TableCell} from '../../../webui/table/TableCell'
import {TableContainer} from '../../../webui/table/TableContainer'
import {TableHead} from '../../../webui/table/TableHead'
import {TableRow} from '../../../webui/table/TableRow'
import {Typography} from '../../../webui/Typography'
import {SerialNumberWarning} from './SerialNumberWarning'

interface InlineMessage {
  text: string
  severity: 'success' | 'error'
}

export const BoxSerialNumbersPage: React.FC = () => {
  const {data, loading, error} = useBoxesForSerialAdminQuery()
  const [clearSerial] = useClearBoxSerialNumberMutation()

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [submitting, setSubmitting] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [message, setMessage] = useState<InlineMessage | null>(null)
  const [excludeText, setExcludeText] = useState('')

  const boxes = useMemo(() => [...(data?.box?.allBoxes ?? [])].sort((a, b) => a.name.localeCompare(b.name)), [data?.box?.allBoxes])
  const withSerialCount = useMemo(() => boxes.filter((box) => box.serialNumber).length, [boxes])

  // Serial numbers pasted into the filter (comma- or whitespace-separated), normalized for comparison.
  const excludedSerials = useMemo(() => {
    const set = new Set<string>()
    for (const token of excludeText.split(/[\s,]+/)) {
      const normalized = token.trim().toUpperCase()
      if (normalized) set.add(normalized)
    }
    return set
  }, [excludeText])

  const visibleBoxes = useMemo(
    () => boxes.filter((box) => !(box.serialNumber && excludedSerials.has(box.serialNumber.toUpperCase()))),
    [boxes, excludedSerials],
  )

  // Only visible boxes that currently have a serial number can be cleared.
  const clearableIds = useMemo(() => visibleBoxes.filter((box) => box.serialNumber).map((box) => box.id), [visibleBoxes])

  if (loading) {
    return <Box m={2}>Loading…</Box>
  }
  if (error) {
    return <Box m={2}>{error.message}</Box>
  }

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const allSelected = clearableIds.length > 0 && clearableIds.every((id) => selectedIds.has(id))
  const someSelected = clearableIds.some((id) => selectedIds.has(id))

  const toggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(clearableIds))
  }

  const onSubmit = async () => {
    const ids = Array.from(selectedIds)
    if (ids.length === 0) return
    setConfirmOpen(false)
    setSubmitting(true)
    setMessage(null)

    const outcomes = await Promise.all(
      ids.map(async (boxId) => {
        try {
          const response = await clearSerial({variables: {boxId}})
          return response.data?.clearBoxSerialNumber?.type === 'Success'
        } catch {
          return false
        }
      }),
    )

    const cleared = outcomes.filter(Boolean).length
    const failed = ids.length - cleared

    setSelectedIds(new Set())
    setSubmitting(false)
    setMessage(
      failed === 0
        ? {text: `Cleared ${cleared} serial number${cleared === 1 ? '' : 's'}.`, severity: 'success'}
        : {text: `Cleared ${cleared} of ${ids.length} selected; ${failed} could not be cleared.`, severity: 'error'},
    )
  }

  const selectedCount = selectedIds.size
  const hiddenCount = boxes.length - visibleBoxes.length

  return (
    <Box m={2}>
      <SerialNumberWarning />
      <Box mt={2}>
        <Typography variant="h4">Box serial numbers</Typography>
      </Box>

      <Box mt={1}>
        <Typography>
          {boxes.length} boxes total · {withSerialCount} with a serial number · {visibleBoxes.length} currently displayed
        </Typography>
      </Box>

      <Box mt={2}>
        <Accordion disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>Hide serial numbers{excludedSerials.size > 0 ? ` (${hiddenCount} hidden)` : ''}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField
              id="exclude-serials"
              label="Serial numbers to hide"
              value={excludeText}
              onChange={(value) => setExcludeText(value)}
              multiline
              minRows={4}
              width={360}
              helperText="Paste serial numbers separated by commas or whitespace. Matching boxes are hidden from the list below."
            />
          </AccordionDetails>
        </Accordion>
      </Box>

      <Box display="flex" alignItems="center" mt={2} mb={1}>
        <ButtonWithLoading
          color="primary"
          loading={submitting}
          onClick={() => setConfirmOpen(true)}
          disabled={submitting || selectedCount === 0}
          minWidth="200px"
        >
          Clear selected serial numbers
        </ButtonWithLoading>
        <Box ml={2}>
          <Typography>{selectedCount} selected</Typography>
        </Box>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allSelected}
                  indeterminate={!allSelected && someSelected}
                  onChange={toggleAll}
                  disabled={clearableIds.length === 0}
                  inputProps={{'aria-label': 'Select all boxes with a serial number'}}
                />
              </TableCell>
              <TableCell>Box</TableCell>
              <TableCell>Serial number</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleBoxes.map((box) => {
              const clearable = !!box.serialNumber
              return (
                <TableRow key={box.id} hover selected={selectedIds.has(box.id)}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedIds.has(box.id)}
                      onChange={() => toggleOne(box.id)}
                      disabled={!clearable || submitting}
                      inputProps={{'aria-label': `Select ${box.name}`}}
                    />
                  </TableCell>
                  <TableCell>{box.name}</TableCell>
                  <TableCell>{box.serialNumber ? box.serialNumber : '—'}</TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {message && (
        <Box mt={3}>
          <Alert severity={message.severity}>{message.text}</Alert>
        </Box>
      )}

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        aria-labelledby="clear-serials-title"
        aria-describedby="clear-serials-description"
      >
        <DialogTitle id="clear-serials-title">Clear serial numbers?</DialogTitle>
        <DialogContent>
          <DialogContentText id="clear-serials-description">
            You are about to clear {selectedCount} serial number{selectedCount === 1 ? '' : 's'}. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} color="primary">
            Cancel
          </Button>
          <ButtonWithLoading loading={submitting} onClick={onSubmit} color="primary" autoFocus>
            Clear {selectedCount} serial number{selectedCount === 1 ? '' : 's'}
          </ButtonWithLoading>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
